import * as React from "react";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { decode } from "html-entities";

import { getOnlineNode } from "../../../core/getNode";
import { paginator } from "../../../core/paginator";
import { alertMessages } from "../../../partials/alertMessages";
import { useEmailDataContext } from "../../../partials/dataContext";
import Pagination from "../../../partials/Pagination";
import MessageBox from "../../../partials/MessageBox";
import DocumentItem from "./DocumentItem";
import SelectedDocumentItem from "./SelectedDocumentItem";
import LoadingSpinner from "../../../partials/loading";
import { getContentTypeByBundle, getTemplateByIdentity } from "../../../utils/helpers";

import { Button, Select, FormControl, InputLabel, MenuItem } from "@mui/material";

export default function Documents(props) {
  const [documentCategory, setDocumentCategory] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPaginating, setIsPaginating] = useState(false);
  const [isError, setIsError] = useState(false);

  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [index, setIndex] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const [categories, setCategories] = useState([]);
  const [selectedDocumentItems, setSelectedDocumentItems] = useState([]);

  const [data, setData] = useState([]);

  const [messageBoxVisible, setMessageBoxVisible] = useState(false);
  const [messageType, setMessageType] = useState("");
  const [messageTitle, setMessageTitle] = useState("");
  const [messageBody, setMessageBody] = useState("");

  const [sessionData, setSessionData] = useState(JSON.parse(localStorage.getItem("sessionData")));

  const { selectedTemplate, contentTypeSelected, setDocumentsObject } = useEmailDataContext();

  // redux
  const system = useSelector((state) => state).systemReducer;
  const config = useSelector((state) => state.configReducer);
  const templateData = getTemplateByIdentity(selectedTemplate, config.templates);
  const contentTypeData = getContentTypeByBundle(contentTypeSelected, templateData.contentTypes);

  useEffect(() => {
    getCategories();
  }, []);

  useEffect(() => {
    getContent();
  }, [currentPage]);

  useEffect(() => {
    if (selectedDocumentItems.length > 0) {
      setDocumentsObject(selectedDocumentItems.map((item) => item.nid));
    } else {
      setDocumentsObject([]);
    }
  }, [selectedDocumentItems]);

  const messageBoxState = (visible, type, title, body) => {
    setMessageBoxVisible(visible);
    setMessageType(type);
    setMessageTitle(title);
    setMessageBody(body);
  };

  const getCategories = () => {
    setIsLoading(true);

    getOnlineNode("api/v2/taxonomies/document_categories")
      .then((response) => {
        renderCategories(response.data);
      })
      .catch((_error) => {
        if (_error.response) {
          messageBoxState(true, "error", "Error", _error.response.statusText);
        } else if (_error.request) {
          messageBoxState(true, "error", "Error", alertMessages.requestError.message);
        } else {
          messageBoxState(true, "error", "Error", alertMessages.unknownError.message);
        }
        setIsLoading(false);
      });
  };

  const renderCategories = (_data) => {
    let _categories = [
      {
        value: 0,
        label: "--- All ---",
      },
    ];

    _data.forEach((item) => {
      _categories.push({
        value: item?.tid[0].value,
        label: item?.name[0].value,
      });
    });
    setCategories(_categories);
  };

  const getContent = () => {
    setIsLoading(true);
    messageBoxState(false, "", "", "");

    let path = "api/v2/documents?status=1&promote=1" + "&items_per_page=" + pageSize + "&page=" + (currentPage - 1);

    const tid = documentCategory;

    if (tid !== 0) {
      path =
        "api/v2/documents/" +
        tid +
        "/all" +
        "?status=1&promote=1" +
        "&items_per_page=" +
        pageSize +
        "&page=" +
        (currentPage - 1);
    }

    getOnlineNode(path)
      .then((response) => {
        setData(response.data.rows);
        setTotalItems(response.data.pager.total_items);
        setIsLoading(false);
        setIsRefreshing(false);
        setIsPaginating(false);
      })
      .catch((_error) => {
        if (_error.response) {
          messageBoxState(true, "error", "Error", _error.response.statusText);
        } else if (_error.request) {
          messageBoxState(true, "error", "Error", alertMessages.requestError.message);
        } else {
          messageBoxState(true, "error", "Error", alertMessages.unknownError.message);
        }
        setIsLoading(false);
      });
  };

  const addSelectedDocumentItem = (_data) => {
    if (
      !(
        selectedDocumentItems.filter((item) => {
          return item.nid === _data.nid;
        }).length > 0
      )
    ) {
      setSelectedDocumentItems((prevItems) => [...prevItems, _data]);
    }
  };

  const removeSelectedDocumentItem = (document) => {
    setSelectedDocumentItems((prevItems) => prevItems.filter((item) => item.nid !== document.nid));
  };

  const renderPagination = () => {
    if (data.length && data.length > 0) {
      return (
        <Pagination
          containerClasses={`d-flex justify-content-center align-items-center`}
          previousDisabled={currentPage === 1 ? true : false}
          nextDisabled={data?.length < pageSize ? true : false}
          currentPage={currentPage}
          onPrevious={() => {
            const current = currentPage - 1;
            setCurrentPage(current);
          }}
          onNext={() => {
            const current = currentPage + 1;
            setCurrentPage(current);
          }}
        />
      );
    }
  };

  const updateDocumentItemsView = (event) => {
    if (event) {
      event.preventDefault();
    }
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      getContent();
    }
  };

  const removeAllSelectedDocumentItems = (event) => {
    if (event) {
      event.preventDefault();
    }
    setSelectedDocumentItems([]);
  };

  const renderCategoryList = categories.map((category) => {
    return (
      <MenuItem
        key={`category-${category.value}`}
        value={category.value}
        onClick={(e) => {
          setDocumentCategory(e.target.value);
        }}
      >
        {decode(category.label)}
      </MenuItem>
    );
  });

  const renderSelectedDocumentItems = selectedDocumentItems.map((document) => (
    <SelectedDocumentItem
      key={`selected-document-item-${document.nid}`}
      document={document}
      removeSelectedDocumentItem={(item) => removeSelectedDocumentItem(item)}
    />
  ));

  const renderDocumentItems = data.map((document) => (
    <DocumentItem
      document={document}
      key={document.nid}
      selectedDocuments={selectedDocumentItems}
      addSelectedDocumentItem={(item) => addSelectedDocumentItem(item)}
      removeSelectedDocumentItem={(item) => removeSelectedDocumentItem(item)}
    />
  ));

  return (
    <div className="document-view-wrapper">
      {isLoading && (
        <div
          className="w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ position: "absolute", top: 0, left: 0, backgroundColor: "rgba(255,255,255,0.5)", zIndex: 9999 }}
        >
          <LoadingSpinner />
        </div>
      )}
      <div className="message-box-wrapper">
        <MessageBox messageBoxVisible={messageBoxVisible} messageTitle={messageTitle} messageBody={messageBody} />
      </div>
      <div className="document-form-filters-wrapper">
        <div className="available-presentations-title">
          <span>Choose Documents</span>
        </div>
        <form className="filter-form">
          <div className="form-item-wrapper input category">
            <FormControl className="filter-select" size="small">
              <InputLabel sx={{ fontSize: "14px" }} size="small" id="category-select-label">
                Category
              </InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={documentCategory}
                label="Category"
                sx={{
                  fontSize: "14px",
                }}
                onChange={(e) => {
                  setDocumentCategory(e.target.value);
                }}
              >
                {renderCategoryList}
              </Select>
            </FormControl>
          </div>
          <div className="form-item-wrapper">
            <div className="form-submit-wrapper form-button-wrapper form-control-wrapper">
              <Button
                type="submit"
                onClick={updateDocumentItemsView}
                variant="contained"
                sx={{
                  backgroundColor: "#1d9bf1",
                  width: "100%",
                  lineHeight: "14px",
                  height: "35px",
                  fontWeight: "500",
                  fontSize: "14px",
                  textTransform: "none",
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </form>
      </div>
      <div id="document-view-content" className="document-view-content">
        <div className="document-items-wrapper">
          <div id="document-items">{renderDocumentItems}</div>
          <div className="">{renderPagination()}</div>
        </div>
        <div className="selected-document-header-wrapper">
          <span>Selected Documents</span>
          {selectedDocumentItems.length > 0 ? (
            <Button
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                fontSize: "14px",
              }}
              className="selected-document-clear-all-button"
              onClick={() => removeAllSelectedDocumentItems()}
            >
              Clear all
            </Button>
          ) : null}
        </div>
        <div id="document-items-selected-by-user-wrapper" className="document-items-selected-by-user-wrapper">
          <div id="document-items-selected-by-user" className="document-items-selected-by-user">
            {selectedDocumentItems.length > 0 ? (
              ""
            ) : (
              <>
                <h5 className="instruction title">INSTRUCTIONS</h5>
              </>
            )}
            {selectedDocumentItems.length > 0 ? (
              <>
                {selectedDocumentItems.map((document, index) => (
                  <SelectedDocumentItem
                    key={`selected-document-item-${index}`}
                    removeSelectedDocumentItem={(document) => removeSelectedDocumentItem(document)}
                    document={document}
                  />
                ))}
              </>
            ) : (
              <>{contentTypeData.help && <>{contentTypeData.help}</>}</>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
